import { endVoiceSession } from "@/lib/assistant-api";
import type { VoiceGatewaySessionDescriptor } from "@/types/assistant";

import { useInstantAssistantStore } from "../useInstantAssistantStore";
import { VoiceTransport } from "./voice-transport";

type DisconnectOptions = {
  keepSession?: boolean;
  reason?: string;
  silent?: boolean;
};

let transport: VoiceTransport | null = null;

// The namespace the active voice session was created in — the end-session
// call must send it back so the gateway can route the session lookup.
let activeVoiceNamespaceId: string | null = null;

export async function connectVoiceGatewaySession(
  descriptor: VoiceGatewaySessionDescriptor,
  namespaceId: string,
) {
  activeVoiceNamespaceId = namespaceId;
  const { actions } = useInstantAssistantStore.getState();
  actions.resetVoiceConnection();
  actions.setVoiceConnectionState("connecting");
  actions.setVoiceError(null);
  actions.setVoiceLevel(0);
  actions.setVoiceMuted(false);

  if (transport) {
    await transport.disconnect();
    transport = null;
  }

  transport = new VoiceTransport({
    onConnectionStateChange: (state) => {
      if (state === "connected") {
        actions.setVoiceConnectionState("connected");
      } else if (state === "connecting" || state === "new") {
        actions.setVoiceConnectionState("connecting");
      } else if (state === "failed" || state === "disconnected" || state === "closed") {
        actions.setVoiceConnectionState("error");
        actions.setVoiceError("Voice session disconnected");
      }
    },
    onError: (error) => {
      actions.setVoiceConnectionState("error");
      actions.setVoiceError(error.message ?? "Voice session failed");
    },
    onLevelUpdate: (level) => {
      actions.setVoiceLevel(level);
    },
  });

  try {
    await transport.connect(descriptor);
    actions.setVoiceConnectionState("connected");
    actions.setVoiceError(null);
    actions.setVoiceStartedAt(new Date().toISOString());
  } catch (error) {
    await transport.disconnect();
    transport = null;
    actions.setVoiceConnectionState("error");
    actions.setVoiceError((error as Error)?.message ?? "Unable to start voice session");
    throw error;
  }
}

export async function disconnectVoiceGatewaySession(options?: DisconnectOptions) {
  const state = useInstantAssistantStore.getState();
  const { actions, voiceSession } = state;

  if (transport) {
    await transport.disconnect();
    transport = null;
  }

  actions.resetVoiceConnection();

  if (!options?.keepSession) {
    actions.setVoiceSession(null);
  }

  if (!options?.silent && voiceSession) {
    if (activeVoiceNamespaceId) {
      try {
        await endVoiceSession(voiceSession.session.voiceSessionId, activeVoiceNamespaceId, {
          reason: options?.reason ?? "user_hangup",
        });
      } catch (error) {
        console.warn("Voice session end failed", error);
      }
    } else {
      console.warn("Voice session end skipped: no namespace recorded for the session");
    }
  }

  activeVoiceNamespaceId = null;
}

export function setVoiceMutedState(muted: boolean) {
  const { actions } = useInstantAssistantStore.getState();
  if (transport) {
    transport.setMuted(muted);
  }
  actions.setVoiceMuted(muted);
}
